import { Component, HostListener, OnDestroy, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MultiFactorResolver } from '@angular/fire/auth';
import { AuthService, MfaRequiredError } from '../../services/auth.service';
import { ScrollLockService } from '../../core/scroll-lock.service';
import { ErrorMapperService } from '../../services/error-mapper.service';
import { ActionThrottleService } from '../../core/action-throttle.service';
import { evaluatePasswordRules, passwordPolicyValidator } from '../../utils/password-policy';
import { modalAnimation } from '../../animations/animations';
import { LoadingModalComponent } from '../loading-modal/loading-modal.component';
import { MfaChallengeComponent } from '../mfa-challenge/mfa-challenge.component';

type AuthMode = 'signin' | 'signup' | 'reset' | 'mfa';

@Component({
  selector: 'app-auth-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LoadingModalComponent, MfaChallengeComponent],
  templateUrl: './auth-modal.component.html',
  styleUrls: ['./auth-modal.component.scss'],
  animations: [modalAnimation]
})
export class AuthModalComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();

  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private errorMapper = inject(ErrorMapperService);
  private throttle = inject(ActionThrottleService);
  private scrollLock = inject(ScrollLockService);

  mode: AuthMode = 'signin';
  authForm: FormGroup;
  loading = false;
  errorMessage = '';
  successMessage = '';
  showPassword = false;

  mfaResolver: MultiFactorResolver | null = null;

  ngOnInit(): void {
    this.scrollLock.lock();
  }

  ngOnDestroy(): void {
    this.scrollLock.unlock();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeModal();
  }

  constructor() {
    this.authForm = this.fb.group({
      displayName: [''],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]]
    });
    this.applyPasswordValidators();
  }

  get isSignUp(): boolean { return this.mode === 'signup'; }
  get displayName() { return this.authForm.get('displayName'); }
  get email() { return this.authForm.get('email'); }
  get password() { return this.authForm.get('password'); }

  /** Live checklist shown under the password field while signing up. */
  get passwordChecks() {
    return evaluatePasswordRules(this.password?.value ?? '');
  }

  /**
   * Password RULES apply only when choosing a password (sign-up). At sign-in
   * we just require a value, so existing members with older passwords aren't
   * locked out. The enforced copy of the rules + the breached-password check
   * run on the server (Cloud Function registerUser).
   */
  private applyPasswordValidators(): void {
    const control = this.authForm.get('password')!;
    control.setValidators(
      this.mode === 'signup'
        ? [Validators.required, passwordPolicyValidator]
        : this.mode === 'reset'
          ? []
          : [Validators.required]
    );
    control.updateValueAndValidity();
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  getErrorMessage(fieldName: string): string {
    const field = this.authForm.get(fieldName);

    if (field?.hasError('required')) {
      if (fieldName === 'email') return 'Email is required';
      if (fieldName === 'password') return 'Password is required';
    }
    if (field?.hasError('email')) {
      return 'Please enter a valid email address';
    }
    if (field?.hasError('passwordPolicy')) {
      return 'Password does not meet all the rules below';
    }
    return '';
  }

  shouldShowError(fieldName: string): boolean {
    const field = this.authForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  setMode(mode: AuthMode) {
    this.mode = mode;
    this.clearMessages();
    this.authForm.reset();
    this.applyPasswordValidators();
  }

  toggleMode() {
    this.setMode(this.mode === 'signup' ? 'signin' : 'signup');
  }

  get canSubmit(): boolean {
    if (this.loading) return false;
    return this.mode === 'reset' ? !!this.email?.valid : this.authForm.valid;
  }

  async onSubmit() {
    if (this.mode === 'reset') {
      this.email?.markAsTouched();
      if (this.email?.invalid) return;
    } else if (this.authForm.invalid) {
      this.authForm.markAllAsTouched();
      return;
    }

    const key = `auth-modal:${this.mode}`;
    const result = await this.throttle.run(key, () => this.performSubmit(), {
      // Sign-in doubles its cooldown after each failure (2s, 4s, 8s … max 60s).
      cooldownMs: this.mode === 'signin' ? 2000 : 5000,
      backoff: this.mode === 'signin',
    }).catch(() => undefined); // errors are already shown by performSubmit

    if (result && result.blocked && result.reason === 'cooldown') {
      this.errorMessage = `Please wait ${this.throttle.waitSeconds(result)}s before trying again.`;
    }
  }

  private async performSubmit(): Promise<void> {
    this.loading = true;
    this.clearMessages();

    try {
      if (this.mode === 'signup') {
        await this.authService.signUp(this.email?.value, this.password?.value, this.displayName?.value);
        this.finish('Account created! Check your inbox to verify your email.');
      } else if (this.mode === 'reset') {
        await this.authService.sendPasswordReset(this.email?.value);
        // Same message whether or not the address has an account.
        this.successMessage = 'If an account exists for that email, a reset link is on its way.';
      } else {
        await this.authService.signIn(this.email?.value, this.password?.value);
        this.finish('Logged in successfully!');
      }
    } catch (error: any) {
      if (this.enterMfaStep(error)) return;
      this.showError(error);
      throw error; // lets the throttle apply its failure back-off
    } finally {
      this.loading = false;
    }
  }

  async signInWithGoogle() {
    const result = await this.throttle.run('auth-modal:google', async () => {
      this.loading = true;
      this.clearMessages();
      try {
        await this.authService.signInWithGoogle();
        this.finish('Signed in with Google!');
      } catch (error: any) {
        if (this.enterMfaStep(error)) return;
        this.showError(error);
      } finally {
        this.loading = false;
      }
    }, { cooldownMs: 3000 });

    if (result.blocked && result.reason === 'cooldown') {
      this.errorMessage = `Please wait ${this.throttle.waitSeconds(result)}s before trying again.`;
    }
  }

  // ── Two-factor step ───────────────────────────────────────────────────────
  private enterMfaStep(error: unknown): boolean {
    if (error instanceof MfaRequiredError) {
      this.mfaResolver = error.resolver;
      this.mode = 'mfa';
      return true;
    }
    return false;
  }

  onMfaVerified() {
    this.mfaResolver = null;
    this.finish('Logged in successfully!');
  }

  cancelMfa() {
    this.mfaResolver = null;
    this.setMode('signin');
  }

  closeModal() {
    this.close.emit();
  }

  private finish(message: string) {
    this.successMessage = message;
    setTimeout(() => this.close.emit(), 1500);
  }

  private showError(error: any) {
    console.error('Auth error:', error?.code ?? error);
    this.errorMessage = this.errorMapper.map(error).userMessage;
  }

  private clearMessages() {
    this.errorMessage = '';
    this.successMessage = '';
  }
}
